import React, { useEffect, useState } from "react";
import {
  Box,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
  VStack,
  Heading,
  SimpleGrid,
} from "@chakra-ui/react";

const stageSteps = [
  { title: "Build", code: "build" },
  { title: "Quotation", code: "quotation" },
  { title: "Order", code: "order" },
  { title: "Bound", code: "bound" },
  { title: "Incepted", code: "incepted" },
  { title: "Lapsed", code: "lapsed" },
];

const sharedInfoSteps = [
  { title: "Request additional info", code: "request_additional_information" },
  { title: "Supply additional info", code: "supply_additional_information" },
];

const transactionMapping = {
  build: [
    { title: "Submission", code: "submission" },
    ...sharedInfoSteps,
    { title: "Decline to participate", code: "decline_to_participate" },
  ],
  quotation: [
    { title: "Quotation request", code: "quotation_request" },
    ...sharedInfoSteps,
    { title: "Decline to quote", code: "decline_to_quote" },
    { title: "Placing cancelled-NTU", code: "placing_cancelledntu" },
    { title: "Quotation", code: "quotation" },
  ],
  order: [
    { title: "Request line/binder", code: "request_for_line_or_binder" },
    ...sharedInfoSteps,
    { title: "Decline line(s)", code: "decline_line_s" },
    { title: "Placing cancelled-NTU", code: "placing_cancelledntu" },
    { title: "Conditional line", code: "conditional_line" },
    { title: "Unconditional line", code: "unconditional_line" },
    { title: "Acceptance of conditions", code: "acceptance_of_conditions" },
    { title: "Decline conditions", code: "decline_conditions" },
    { title: "Signed line advice", code: "signed_line_advice" },
  ],
  bound: [],
  incepted: [],
  lapsed: [],
};

const PlacingStepper = ({
  stageCode,
  transactionCode,
  size = "sm",
  heading = null,
  defaultOrientation = "horizontal",
}) => {
  const [showSubSteps, setShowSubSteps] = useState(false);

  const stageIndex = stageSteps.findIndex(
    (s) => s.code === stageCode?.toLowerCase()
  );
  const { activeStep: activeStage, setActiveStep: setStageStep } = useSteps({
    index: stageIndex >= 0 ? stageIndex : 0,
    count: stageSteps.length,
  });

  const sizeMap = {
    xs: { circle: 3, font: "2xs", title: "2xs" },
    sm: { circle: 4, font: "xs", title: "xs" },
    md: { circle: 5, font: "sm", title: "sm" },
    lg: { circle: 6, font: "md", title: "md" },
    xl: { circle: 8, font: "lg", title: "lg" },
  };
  const resolvedSize = sizeMap[size] || sizeMap.sm;
  const subStepperSize = sizeMap["xs"];

  useEffect(() => {
    setStageStep(stageIndex >= 0 ? stageIndex : 0);
  }, [stageIndex, setStageStep]);

  return (
    <VStack spacing={6} align="stretch">
      {heading && (
        <Heading size={size === "sm" ? "sm" : "md"} textAlign="center">
          {heading}
        </Heading>
      )}

      {/* MAIN STAGES (proper Stepper) */}
      <Stepper
        size={size}
        index={activeStage}
        colorScheme="blue"
        orientation={defaultOrientation}
        gap="1"
        justify="space-between">
        {stageSteps.map((step, idx) => (
          <Step key={idx}>
            <StepIndicator
              cursor="pointer"
              onClick={() => setShowSubSteps((prev) => !prev)} // Toggle sub-steps
            >
              <StepStatus
                complete={
                  <StepIcon
                    boxSize={resolvedSize.circle}
                    colorPalette="blue.500"
                  />
                }
                incomplete={<StepNumber fontSize={resolvedSize.font} />}
                active={<StepNumber fontSize={resolvedSize.font} />}
              />
            </StepIndicator>
            <Box flexShrink="0">
              <StepTitle fontSize={resolvedSize.title}>{step.title}</StepTitle>
            </Box>
          </Step>
        ))}
      </Stepper>

      {/* SUB-STEPS GRID */}
      {showSubSteps && (
        <SimpleGrid columns={stageSteps.length} spacing={4} pt={2}>
          {stageSteps.map((stage) => {
            const subSteps = transactionMapping[stage.code] || [];
            const subIndex =
              stage.code === stageCode?.toLowerCase()
                ? subSteps.findIndex((s) => s.code === transactionCode)
                : -1;

            return (
              <Box key={stage.code}>
                {subSteps.length > 0 && (
                  <Stepper
                    size="xs"
                    index={subIndex}
                    colorScheme="gray"
                    orientation="vertical"
                    gap="1">
                    {subSteps.map((subStep, sIdx) => (
                      <Step key={sIdx}>
                        <StepIndicator>
                          <StepStatus
                            complete={
                              <StepIcon boxSize={subStepperSize.circle} />
                            }
                            incomplete={
                              <StepNumber fontSize={subStepperSize.font} />
                            }
                            active={
                              <StepNumber fontSize={subStepperSize.font} />
                            }
                          />
                        </StepIndicator>
                        <Box flexShrink="0">
                          <StepTitle fontSize={subStepperSize.title}>
                            {subStep.title}
                          </StepTitle>
                        </Box>
                      </Step>
                    ))}
                  </Stepper>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      )}
    </VStack>
  );
};

export default PlacingStepper;
